/**
 * Раскладка строки грида: треки CSS-grid, sticky-офсеты и края закреплённых зон.
 *
 * Строка — это grid БЕЗ зазоров между треками: расстояние между колонками дают
 * горизонтальные паддинги самих ячеек, а отступ от края строки — увеличенный
 * паддинг крайних. Так закреплённые ячейки смыкаются встык (сквозь зазор не
 * видно едущий контент) и достают до края зоны без компенсаций, а sticky-офсет
 * становится обычной суммой ширин предыдущих треков.
 *
 * Единицы измерения задаёт вызывающий (значения дублируются в SCSS через
 * CSS-переменные, см. `dataTableLayoutVars`).
 */

/** Один трек строки: служебный (чекбокс, действия) или колонка данных. */
export interface ILayoutTrack {
    id: string;
    /** Ширина в px; null — гибкий трек (fr), он не может быть закреплён. */
    width: number | null;
    /** Сторона закрепления; undefined — трек едет вместе с содержимым. */
    pinned?: 'left' | 'right';
}

export interface ILayoutMetrics {
    /** Паддинг ячейки с каждой стороны: два соседних дают видимое расстояние между колонками. */
    padCell: number;
    /** Паддинг крайней ячейки со стороны края строки. */
    padEdge: number;
}

/** Полная ширина трека: ширина содержимого плюс паддинги ячейки. */
export const trackWidth = (contentWidth: number, { padCell, padEdge }: ILayoutMetrics, edge?: 'start' | 'end') =>
    contentWidth + padCell + (edge ? padEdge : padCell);

/** Позиция одной закреплённой ячейки. */
export interface IStickySlot {
    side: 'left' | 'right';
    /** Значение для `left`/`right`: где ячейка замирает при прокрутке. */
    offset: number;
    /** Крайняя ячейка зоны — на ней скругление «острова» строки. */
    edge: boolean;
}

export interface IStickyLayout {
    slots: Record<string, IStickySlot>;
    /** id последней закреплённой слева и первой закреплённой справа — на них
     *  рисуется разделитель зоны. */
    leftEdgeId?: string;
    rightEdgeId?: string;
}

/**
 * Офсеты закреплённых треков.
 *
 * Инварианты:
 * - зазоров между треками нет, поэтому офсет = сумма ширин предыдущих треков;
 * - первая закреплённая слева и последняя справа прилипают к самому краю зоны
 *   прокрутки (offset 0) — отступ содержимого от края даёт паддинг крайней ячейки.
 */
export function computeStickyLayout(tracks: ILayoutTrack[]): IStickyLayout {
    const slots: Record<string, IStickySlot> = {};
    const left = tracks.filter((t) => t.pinned === 'left');
    const right = tracks.filter((t) => t.pinned === 'right');

    let cursor = 0;
    for (const track of tracks) {
        if (track.pinned === 'left') {
            slots[track.id] = { side: 'left', offset: cursor, edge: left.indexOf(track) === 0 };
        }
        cursor += track.width ?? 0;
    }

    let rcursor = 0;
    for (const track of [...tracks].reverse()) {
        if (track.pinned === 'right') {
            slots[track.id] = {
                side: 'right',
                offset: rcursor,
                edge: right.indexOf(track) === right.length - 1,
            };
        }
        rcursor += track.width ?? 0;
    }

    return {
        slots,
        leftEdgeId: left.length ? left[left.length - 1].id : undefined,
        rightEdgeId: right.length ? right[0].id : undefined,
    };
}

/** Минимальная ширина строки: сумма треков (паддинги уже внутри них). */
export function computeRowMinWidth(trackMins: number[]): number {
    return trackMins.reduce((acc, w) => acc + w, 0);
}

/** Офсеты закреплённых треков CSS-переменными контейнера таблицы: ячейки ссылаются на переменную, поэтому смена
 *  ширины закреплённой колонки меняет только значения на контейнере, а не стиль каждой ячейки. Имя — по стороне и
 *  порядку в зоне, а не по id колонки (id — произвольное имя поля). */
export interface IStickyOffsetVars {
    /** id трека → имя переменной. */
    names: Record<string, string>;
    /** имя переменной → значение в px (для `style` контейнера). */
    values: Record<string, string>;
}

export function computeStickyOffsetVars(layout: IStickyLayout): IStickyOffsetVars {
    const names: Record<string, string> = {};
    const values: Record<string, string> = {};
    const count = { left: 0, right: 0 };
    for (const [id, slot] of Object.entries(layout.slots)) {
        const name = `--dtb-pin-${slot.side === 'left' ? 'l' : 'r'}${count[slot.side]++}`;
        names[id] = name;
        values[name] = `${slot.offset}px`;
    }
    return { names, values };
}
